import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AccessedProjectsComponent } from '../accessed-projects/accessed-projects.component';
import { faEye, faDownload, faSearch, faClock, faUsers, faCalendarAlt, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { PageContainerService, TimerService } from '../../services';
import { ControllerForViewBase } from '../framework/controller-for-view-base';
import { UserSessionsCommandRequest, UserSessionsCommandResponse, ExportUserSessionsExcelCommandRequest, ExportUserSessionsExcelCommandResponse } from '../../data/CommandServiceDtos.generated';

@Component({
  standalone: false,
  selector: 'app-user-sessions',
  templateUrl: './user-sessions.component.html',
  styleUrls: ['./user-sessions.component.scss'],
})
export class UserSessionsComponent extends ControllerForViewBase implements OnInit {
  faEye = faEye;
  faDownload = faDownload;
  faSearch = faSearch;
  faClock = faClock;
  faUsers = faUsers;
  faCalendarAlt = faCalendarAlt;
  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;
  users = [
    { id: 0, name: 'All Users' }
  ];

  dateRanges = ['All Time', 'This Week', 'This Month', 'Custom'];

  selectedUserId = 0;
  selectedDateRange = 'This Week';
  toDate: string | null = null;
  fromDate: string | null = null;
  selectedGroupingOption: string = '';
  totalActiveHours: string = '0h 0m';
  isLoading = false;
  isExporting = false;

  sessions: Array<any> = [];
  displayedSessions: Array<any> = [];
  displayedSessionsPaged: Array<any> = [];

  // paging
  page: number = 1;
  pageSize: number = 10;
  totalItems: number = 0;
  pager: { pages: number[]; pageSize: number; totalItems: number; currentPage: number; totalPages: number } = { pages: [], pageSize: 10, totalItems: 0, currentPage: 1, totalPages: 1 };

  constructor(public dialog: MatDialog, public pcs: PageContainerService, public timerService: TimerService) { super(pcs); }

  ngOnInit(): void {
    this.timerService.resetTimer();
    // Rely on server-side pagination; load initial page
    this.loadUserSession();
    this.loadUsers();
  }

  async loadUsers() {
    const usersResponse = await (this.pcs.apiService as any).GetSessionUsers({});
    const users = usersResponse && usersResponse.Result ? usersResponse.Result.map(u => ({ id: u.Id || u.id, name: u.Username })) : [];
    this.users = [{ id: 0, name: 'All Users' }].concat(users);
  }
  async loadUserSession() {
    this.isLoading = true;
    const request = new UserSessionsCommandRequest();
    // ensure filter and pager exist
    request.Filter = request.Filter || ({} as any);
    request.Filter.Pager = request.Filter.Pager || ({} as any);
    request.Filter.Pager.PageSize = this.pageSize;
    request.Filter.Pager.CurrentPage = this.page;
    // apply selected user (1 == All Users)
    if (this.selectedUserId && this.selectedUserId !== 0) request.Filter.UserId = this.selectedUserId;
    // apply date range
    if (this.selectedDateRange === 'This Week') {
      request.Filter.FromDate = new Date(new Date().getTime() - 7 * 24 * 3600 * 1000);
      request.Filter.ToDate = new Date();
    } else if (this.selectedDateRange === 'This Month') {
      const now = new Date();
      request.Filter.FromDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      request.Filter.ToDate = new Date();
    } else if (this.selectedDateRange === 'Custom') {
      if (this.fromDate) request.Filter.FromDate = new Date(this.fromDate);
      if (this.toDate) request.Filter.ToDate = new Date(this.toDate);
    }

    try {
      const getCommandResponse: UserSessionsCommandResponse = await this.pcs.apiService.UserSessions(request);
      const paged = getCommandResponse && getCommandResponse.result ? getCommandResponse.result : null;
      this.displayedSessionsPaged = ((paged && paged.Data) || []).map((s: any) => {
        if (s.SessionStart && typeof s.SessionStart === 'string' && !s.SessionStart.endsWith('Z') && !s.SessionStart.includes('+')) {
          s.SessionStart += 'Z';
        }
        if (s.LastPostback && typeof s.LastPostback === 'string' && !s.LastPostback.endsWith('Z') && !s.LastPostback.includes('+')) {
          s.LastPostback += 'Z';
        }
        return s;
      });
      this.totalItems = (paged && paged.Pager && paged.Pager.TotalItems) || 0;
      this.calculateTotalActiveHours();
      this.pager.pageSize = this.pageSize;
      this.pager.totalItems = this.totalItems;
      this.pager.totalPages = Math.max(1, Math.ceil(this.totalItems / this.pageSize));
      this.pager.currentPage = this.page;
      this.pager.pages = [];
      for (let i = 1; i <= this.pager.totalPages; i++) this.pager.pages.push(i);
    } finally {
      this.isLoading = false;
    }
  }
  onDateRangeChange() {
    if (this.selectedDateRange !== 'Custom') {
      this.fromDate = null;
      this.toDate = null;
    }
  }

  search() {
    // delegate filtering to server-side; reset to first page
    this.page = 1;
    this.loadUserSession();
  }

  openAccessedProjects(session: any) {
    this.dialog.open(AccessedProjectsComponent, {
      data: { session },
      width: '920px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100vh - 32px)',
      panelClass: 'accessed-projects-dialog'
    });
  }

  setPage(page: number) {
    if (page < 1) page = 1;
    if (page > this.pager.totalPages) page = this.pager.totalPages;
    if (this.page === page) return;
    this.page = page;
    this.loadUserSession();
  }

  prevPage() { if (this.page > 1) { this.page--; this.loadUserSession(); } }
  nextPage() { if (this.page < this.pager.totalPages) { this.page++; this.loadUserSession(); } }

  calculateTotalActiveHours() {
    let totalMinutes = 0;
    this.displayedSessionsPaged.forEach(s => {
      if (s.UserActiveHours) {
        const parts = s.UserActiveHours.split(':');
        if (parts.length >= 2) {
          totalMinutes += parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
        }
      }
    });
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    this.totalActiveHours = (hours > 0 ? hours + 'h ' : '') + minutes + 'm';
  }

  getFirstRecord(): number {
    return this.totalItems === 0 ? 0 : ((this.page - 1) * this.pageSize) + 1;
  }

  getLastRecord(): number {
    return Math.min(this.page * this.pageSize, this.totalItems);
  }

  getResultSummary(): string {
    if (this.totalItems === 0) return 'No records for the selected filters';
    return this.totalItems + (this.totalItems === 1 ? ' session found' : ' sessions found');
  }

  async downloadReport() {
    if (this.isExporting) return;
    this.isExporting = true;
    const request = new ExportUserSessionsExcelCommandRequest() as any;
    if (this.selectedUserId && this.selectedUserId !== 0) request.UserId = this.selectedUserId;

    if (this.selectedDateRange === 'This Week') {
      request.FromDate = new Date(new Date().getTime() - 7 * 24 * 3600 * 1000);
      request.ToDate = new Date();
    } else if (this.selectedDateRange === 'This Month') {
      const now = new Date();
      request.FromDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      request.ToDate = new Date();
    } else if (this.selectedDateRange === 'Custom') {
      if (this.fromDate) request.FromDate = new Date(this.fromDate);
      if (this.toDate) request.ToDate = new Date(this.toDate);
    }

    request.GroupBy = this.selectedGroupingOption;
    request.CurrentUsername = this.pcs.clientSessionService.userName;
    request.TimezoneOffsetFromGMT = new Date().getTimezoneOffset() * -1; // result in minutes, same as moment().utcOffset()

    try {
      const response = await this.pcs.apiService.ExportUserSessionsExcel(request) as any;
      if (response) {
        const reportData = response.FileBytes;
        const fileName = response.FileName || 'UserSessionsReport.xlsx';

        if (reportData) {
          const byteCharacters = atob(reportData);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

          this.downloadExportFile(blob, fileName);
        }
      }
    } finally {
      this.isExporting = false;
    }
  }

  private downloadExportFile(blob: Blob, fileName: string): void {
    const a = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    a.href = objectUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  }
}
